// File: components/TheSlide.tsx
import React, { useState, useEffect, useRef } from 'react';
import { PlayerProfile, IdolRank } from '../types';
import { ExitButton } from './ExitButton';
import { MusicManager } from './audio/GameAudioManager';
import { ArrowLeft, ArrowRight, Wind, DollarSign, Users } from 'lucide-react';
import { Notification } from './NotificationSystem';
import { RANKS } from '../constants';

// Authentic character renderers
import { drawRumi } from './renderer/player/drawRumi';
import { drawZoey } from './renderer/allies/drawZoey';
import { drawMira } from './renderer/allies/drawMira';
import { Ally, Player as PlayerEntity } from './types/entities';
import { CostumeId } from '../types';

interface TheSlideProps {
    player: PlayerProfile;
    onUpdatePlayer: (updates: Partial<PlayerProfile>) => void;
    onExit: () => void;
    onNotify: (message: string, type?: Notification['type']) => void;
}

const CANVAS_WIDTH = 400;
const CANVAS_HEIGHT = 600;

export const TheSlide: React.FC<TheSlideProps> = ({ player, onUpdatePlayer, onExit, onNotify }) => {
    const [score, setScore] = useState(0);
    const [gameOver, setGameOver] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const animationFrameRef = useRef<number>(0);

    // Game State Refs (Decoupled from React Render Cycle for stability)
    const isRunningRef = useRef(false);
    const playerX = useRef(CANVAS_WIDTH / 2);
    const obstacles = useRef<{ x: number, y: number, type: 'WALL' | 'BALL' }[]>([]);
    const scoreRef = useRef(0);
    const speedRef = useRef(5);

    const startGame = async () => {
        await MusicManager.initialize();

        // Reset State
        setGameOver(false);
        setIsPlaying(true);
        setScore(0);

        // Reset Refs
        isRunningRef.current = true;
        scoreRef.current = 0;
        speedRef.current = 4;
        obstacles.current = [];
        playerX.current = CANVAS_WIDTH / 2;

        if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);

        // Start Game Loop
        loop();
    };

    const movePlayer = (direction: 'LEFT' | 'RIGHT') => {
        if (!isRunningRef.current) return;
        const moveAmount = 80;
        const newX = direction === 'LEFT' ? playerX.current - moveAmount : playerX.current + moveAmount;

        // Clamp with wider margin
        if (newX > 30 && newX < CANVAS_WIDTH - 30) {
            playerX.current = newX;
            // Play Squeak Sound
            MusicManager.playSFX("noiseSynth", "32n"); // "32n" treated as duration for NoiseSynth
        }
    };

    const spawnObstacle = () => {
        const lanes = [CANVAS_WIDTH / 2 - 80, CANVAS_WIDTH / 2, CANVAS_WIDTH / 2 + 80];

        // Impossible Scenario Prevention:
        // Collect lanes occupied by WALLs that are still near the top (spawn area)
        const activeWallLanes = obstacles.current
            .filter(obs => obs.type === 'WALL' && obs.y > CANVAS_HEIGHT - 100)
            .map(obs => obs.x);

        // If 2 lanes are already blocked, the 3rd MUST be free or a BALL
        let availableLanes = lanes;
        if (activeWallLanes.length >= 2) {
            availableLanes = lanes.filter(l => !activeWallLanes.includes(l));
        }

        const lane = availableLanes[Math.floor(Math.random() * availableLanes.length)];

        // Even with 1 lane available, we don't want a 100% wall rate if it's the only path
        const isWall = activeWallLanes.length >= 2 ? false : Math.random() < 0.8;

        obstacles.current.push({
            x: lane,
            y: CANVAS_HEIGHT + 50,
            type: isWall ? 'WALL' : 'BALL'
        });
    };

    const drawGame = (ctx: CanvasRenderingContext2D) => {
        // Background (Colorful Slides from movie)
        ctx.fillStyle = "#111"; ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

        const drawSlide = (x: number, color: string) => {
            ctx.fillStyle = color;
            ctx.globalAlpha = 0.8;
            ctx.fillRect(x - 45, 0, 90, CANVAS_HEIGHT);
            ctx.strokeStyle = "rgba(255,255,255,0.3)";
            ctx.lineWidth = 4;
            ctx.strokeRect(x - 45, 0, 90, CANVAS_HEIGHT);
            ctx.globalAlpha = 1.0;
        };

        drawSlide(CANVAS_WIDTH / 2 - 80, "#dc2626"); // Red/Pink
        drawSlide(CANVAS_WIDTH / 2, "#ca8a04");      // Yellow
        drawSlide(CANVAS_WIDTH / 2 + 80, "#0891b2"); // Cyan

        // Speed Lines
        ctx.strokeStyle = "rgba(255,255,255,0.1)";
        ctx.lineWidth = 2;
        const offset = (Date.now() / 2) % 50;
        for (let y = 0; y < CANVAS_HEIGHT; y += 50) {
            ctx.beginPath(); ctx.moveTo(0, y - offset); ctx.lineTo(CANVAS_WIDTH, y - offset); ctx.stroke();
        }

        // --- DRAW AUTHENTIC TACTICAL SQUAD ---
        const costume: CostumeId = 'LEATHER';
        const squadY = 100; // Alignment with collision detection
        const charHeight = 80; // Size on screen
        const mockPlayer: PlayerEntity = {
            x: playerX.current - 15,
            y: squadY - charHeight,
            width: 30,
            height: charHeight,
            color: "",
            vx: 0,
            vy: 0,
            isSwinging: false,
            swingTimer: 0,
            facing: 1
        };
        const mockMira: Ally = {
            x: playerX.current - 48,
            y: squadY - charHeight + 5,
            width: 30,
            height: charHeight - 5,
            color: "",
            attackCooldown: 0,
            targetX: 0,
            isAttacking: false,
            attackTimer: 0
        };
        const mockZoey: Ally = {
            x: playerX.current + 18,
            y: squadY - charHeight + 5,
            width: 30,
            height: charHeight - 5,
            color: "",
            attackCooldown: 0,
            targetX: 0,
            isAttacking: false,
            attackTimer: 0
        };
        // Draw squad in depth order (Allies slightly behind Rumi)
        drawMira(ctx, mockMira, costume, true);
        drawZoey(ctx, mockZoey, costume, true);
        drawRumi(ctx, mockPlayer, false, costume, true);

        // Obstacles
        obstacles.current.forEach(obs => {
            if (obs.type === 'WALL') {
                ctx.fillStyle = "#ef4444";
                ctx.fillRect(obs.x - 20, obs.y - 20, 40, 40);
                ctx.fillStyle = "#b91c1c"; ctx.fillRect(obs.x - 15, obs.y - 15, 30, 30);
            } else {
                // Ball Pit Balls (Colorful)
                const colors = ["#facc15", "#ef4444", "#3b82f6", "#ec4899", "#22c55e", "#a855f7"];
                const color = colors[Math.floor((obs.x + obs.y) % colors.length)];
                ctx.fillStyle = color;
                ctx.beginPath(); ctx.arc(obs.x, obs.y, 20, 0, Math.PI * 2); ctx.fill();
                ctx.strokeStyle = "rgba(255,255,255,0.5)"; ctx.lineWidth = 2; ctx.stroke();
                // Shine
                ctx.fillStyle = "#fff"; ctx.globalAlpha = 0.4;
                ctx.beginPath(); ctx.arc(obs.x - 6, obs.y - 6, 6, 0, Math.PI * 2); ctx.fill();
                ctx.globalAlpha = 1.0;
            }
        });
    };

    const loop = () => {
        if (!isRunningRef.current) return;

        const ctx = canvasRef.current?.getContext('2d');
        if (!ctx) return;

        // Update Difficulty: EVEN SLOWER speed increment
        speedRef.current += 0.001;

        // Spawn Logic: Lowered base spawn rate
        if (Math.random() < 0.012 + (scoreRef.current * 0.0000005)) {
            spawnObstacle();
        }

        // Physics
        for (let i = obstacles.current.length - 1; i >= 0; i--) {
            const obs = obstacles.current[i];
            obs.y -= speedRef.current; // Move UP (falling down effect)

            // Collision Check
            const dy = Math.abs(obs.y - 100); // Player is at Y=100
            const dx = Math.abs(obs.x - playerX.current);

            if (dy < 30 && dx < 30) { // Hitbox check: More forgiving (was 35)
                if (obs.type === 'WALL') {
                    handleGameOver();
                    return;
                } else {
                    // BUFF: Bonus Ball increased from 500 to 10,000
                    scoreRef.current += 10000;
                    obstacles.current.splice(i, 1);
                    MusicManager.playSFX("uiSynth", "C6", "16n");
                    continue;
                }
            }

            if (obs.y < -50) {
                obstacles.current.splice(i, 1);
                // BUFF: Distance bonus increased from 100 to 1,000
                scoreRef.current += 1000;
            }
        }

        setScore(Math.floor(scoreRef.current));

        // Draw
        ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
        drawGame(ctx);

        animationFrameRef.current = requestAnimationFrame(loop);
    };

    const handleGameOver = () => {
        isRunningRef.current = false;
        cancelAnimationFrame(animationFrameRef.current);
        setGameOver(true);

        const rankData = RANKS[player.rank] || RANKS[IdolRank.TRAINEE];
        const multiplier = rankData?.multiplier || 1;
        const finalScorePoints = Math.floor(scoreRef.current * multiplier);
        // BUFF: Increased ratio from score/10 to score (1:1)
        const cashReward = finalScorePoints;

        onUpdatePlayer({
            fans: player.fans + finalScorePoints,
            cash: player.cash + cashReward
        });
        onNotify(`Show Finished! +${finalScorePoints.toLocaleString()} Fans, +$${cashReward.toLocaleString()} Cash`, 'success');
    };

    useEffect(() => {
        // Initial render for the "START" state
        const ctx = canvasRef.current?.getContext('2d');
        if (ctx && !isPlaying && !gameOver) {
            ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
            drawGame(ctx);
        }
    }, [isPlaying, gameOver]);

    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft') movePlayer('LEFT');
            if (e.key === 'ArrowRight') movePlayer('RIGHT');
        };
        window.addEventListener('keydown', handleKey);

        return () => {
            window.removeEventListener('keydown', handleKey);
        };
    }, []);

    useEffect(() => {
        return () => {
            isRunningRef.current = false;
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, []);

    return (
        <div className="h-full w-full bg-slate-900 flex flex-col items-center justify-center p-4 relative overflow-hidden">
            <ExitButton onExit={onExit} />

            <div className="relative z-10 w-full max-w-lg glass-panel px-4 py-2 md:p-4 rounded-xl flex flex-col items-center h-full max-h-full overflow-y-auto">
                <header className="mb-2 text-center shrink-0">
                    <h2 className="text-xl md:text-2xl font-heading text-white italic">THE SLIDE</h2>
                    <p className="text-[9px] md:text-[10px] text-slate-400">"The leather has betrayed us!" - Avoid walls, collect balls.</p>
                </header>

                <div className="relative flex-1 bg-black rounded-lg border-4 border-slate-700 overflow-hidden w-full max-w-[400px]">
                    <canvas ref={canvasRef} width={CANVAS_WIDTH} height={CANVAS_HEIGHT} className="w-full h-full object-contain" />

                    {!isPlaying && !gameOver && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30 backdrop-blur-[1px]">
                            <button onClick={startGame} className="px-8 py-4 bg-pink-600 hover:bg-pink-500 text-white font-bold rounded-full animate-bounce shadow-2xl border-2 border-white/20">
                                JUMP IN!
                            </button>
                        </div>
                    )}

                    {gameOver && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 z-20">
                            <h3 className="text-red-500 text-4xl font-heading mb-4">WIPEOUT</h3>
                            <div className="bg-slate-900/80 p-6 rounded-xl border border-slate-700 space-y-4 mb-6">
                                <div className="flex items-center justify-between gap-8">
                                    <div className="flex items-center gap-2 text-pink-400 font-bold"><Users size={20} /> Fans</div>
                                    <div className="text-white font-mono text-xl">+{(scoreRef.current * (RANKS[player.rank]?.multiplier || 1)).toLocaleString()}</div>
                                </div>
                                <div className="flex items-center justify-between gap-8 border-t border-slate-800 pt-4">
                                    <div className="flex items-center gap-2 text-green-400 font-bold"><DollarSign size={20} /> Cash</div>
                                    <div className="text-white font-mono text-xl">+{(scoreRef.current * (RANKS[player.rank]?.multiplier || 1)).toLocaleString()}</div>
                                </div>
                            </div>
                            <button
                                onClick={startGame}
                                className="px-6 py-3 bg-white hover:bg-gray-200 text-black font-bold rounded-full shadow-lg transform transition hover:scale-105 active:scale-95"
                            >
                                SLIDE AGAIN
                            </button>
                        </div>
                    )}

                    <div className="absolute top-4 right-4 text-2xl font-mono font-bold text-white drop-shadow-md z-10 bg-black/40 px-3 py-1 rounded">
                        {score.toLocaleString()}
                    </div>
                </div>

                <div className="mt-4 md:mt-6 flex gap-4 w-full justify-center shrink-0">
                    <button onMouseDown={() => movePlayer('LEFT')} className="p-4 md:p-6 bg-slate-800 rounded-full active:bg-slate-700 border border-slate-600 shadow-lg transition-transform active:scale-90">
                        <ArrowLeft size={28} />
                    </button>
                    <button onMouseDown={() => movePlayer('RIGHT')} className="p-4 md:p-6 bg-slate-800 rounded-full active:bg-slate-700 border border-slate-600 shadow-lg transition-transform active:scale-90">
                        <ArrowRight size={28} />
                    </button>
                </div>
            </div>
        </div>
    );
};
